import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DirectUploadProvider } from '@docflow/react-activestorage-provider';
import { Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { apiClient, tokenStorage } from '../../../utils/api-client';

interface ScanUploadDialogProps {
  /** Uploads the photo, once ActiveStorage has a signed id for it. */
  onUpload: (signedId: string, climbType: 'boulder' | 'route') => Promise<void>;
}

/**
 * Uploads a photo of a wall to be scanned. The climb type is asked for up front
 * because it decides what the scan produces: a boulder is turned into circled
 * holds, a route into a line, and there is no telling which from the photo.
 */
export function ScanUploadDialog({ onUpload }: ScanUploadDialogProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [climbType, setClimbType] = useState<'boulder' | 'route'>('boulder');
  const [saving, setSaving] = useState(false);

  const getApiOrigin = useCallback(() => {
    const source = apiClient.getBaseURL() || window.location.href;
    try {
      const url = new URL(source);
      return { host: url.hostname, port: url.port || undefined, protocol: url.protocol.replace(':', '') };
    } catch {
      return {
        host: window.location.hostname,
        port: window.location.port || undefined,
        protocol: window.location.protocol.replace(':', ''),
      };
    }
  }, []);

  async function handleSignedId(signedIds: string[]) {
    if (signedIds.length === 0) return;
    setSaving(true);
    try {
      await onUpload(signedIds[0], climbType);
      setOpen(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="w-full gap-1.5">
          <Plus className="size-4" aria-hidden />
          {t('gymAdmin.scans.newScan')}
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('gymAdmin.scans.newScan')}</DialogTitle>
          <DialogDescription>{t('gymAdmin.scans.newScanHint')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t('gymAdmin.scans.climbTypeLabel')}</Label>
            <div className="flex gap-2">
              {(['boulder', 'route'] as const).map((type) => (
                <button
                  key={type}
                  type="button"
                  aria-pressed={climbType === type}
                  onClick={() => setClimbType(type)}
                  className={`flex-1 rounded-md border px-3 py-2 text-sm transition-colors ${
                    climbType === type
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-input hover:bg-muted'
                  }`}
                >
                  {t(`gymAdmin.scans.climbType.${type}`)}
                </button>
              ))}
            </div>
          </div>

          <DirectUploadProvider
            origin={getApiOrigin()}
            headers={{ Authorization: `Bearer ${tokenStorage.getToken() || ''}` }}
            onSuccess={handleSignedId}
            render={({ handleUpload, uploads, ready }: {
              handleUpload: (files: FileList) => void;
              uploads: Array<{ state: string }>;
              ready: boolean;
            }) => {
              const uploading = uploads.some((upload) => upload.state === 'uploading') || saving;

              return (
                <div className="space-y-2">
                  <input
                    id="scan-photo-input"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={!ready || uploading}
                    onChange={(event) => {
                      const { files } = event.target;
                      if (files && files.length > 0) handleUpload(files);
                      event.target.value = '';
                    }}
                  />
                  <Button
                    type="button"
                    className="w-full"
                    disabled={!ready || uploading}
                    onClick={() => document.getElementById('scan-photo-input')?.click()}
                  >
                    {uploading && <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />}
                    {uploading ? t('gymAdmin.scans.uploading') : t('gymAdmin.scans.choosePhoto')}
                  </Button>
                </div>
              );
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
